import {
  AngebotsId,
  GlobalAction,
  GlobalActions,
  GlobalEventDispatcher,
  GlobalPut,
  Treuebonus,
  TreuebonusMaxReceived,
} from '@ps20-frontend/shop-integration-redux';
import { call, put, select, take, takeEvery } from 'redux-saga/effects';
import cloneDeep from 'lodash/cloneDeep';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { App } from '@ps20-frontend/frontend-models';
import { IAppState } from '../toolbarState';
import { ToolbarActions } from '../toolbar.actions';
import { getIndexForToolbarElement } from '../interfaces/getIndexByAppOrder';

export function* watchRabattHinweis() {
  yield takeEvery(
    GlobalActions.TREUEBONUS_MAX_RECEIVED,
    safeSaga(treuebonusWatch, App.SHOP_TOOLBAR),
  );
  yield takeEvery(
    GlobalActions.VERTRAGSLAUFZEIT_CHANGED,
    safeSaga(laufzeitWatch, App.SHOP_TOOLBAR),
  );
  yield takeEvery(
    GlobalActions.VEREINBARUNGEN_CHANGED,
    safeSaga(vereinbarungenWatch, App.SHOP_TOOLBAR),
  );
}

export function* treuebonusWatch(action: GlobalAction<TreuebonusMaxReceived>) {
  const globalEventDispatcher = GlobalEventDispatcher.getInstance();
  const globalPut = GlobalPut.getInstance();
  const state: IAppState = yield select();

  yield call(globalPut.put, new GlobalActions().requestAngeboteInWarenkorb());
  const angeboteInWarenkorbAction: GlobalAction<Array<AngebotsId>> = yield take(
    GlobalActions.ANGEBOTE_IN_WARENKORB_PROVIDED,
  );
  const angeboteInWarenkorb = angeboteInWarenkorbAction.payload;
  const actionPayload: TreuebonusMaxReceived = action.payload;
  let newTreueBonusArray = getNewTreuebonusArray(actionPayload, state.treuebonus);
  if (actionPayload.treuebonusMax > 0) {
    newTreueBonusArray = newTreueBonusArray.sort(
      (a, b) =>
        getIndexForToolbarElement(a, angeboteInWarenkorb) -
        getIndexForToolbarElement(b, angeboteInWarenkorb),
    );
  }
  yield put(new ToolbarActions().treuebonusUpdated(newTreueBonusArray));

  if (state.gesamtpreisPopover.vereinbarungenInfoChanged) {
    globalEventDispatcher.dispatch(new GlobalActions().rabattHinweisChanged(true));
  }
}

export function* laufzeitWatch() {
  const globalEventDispatcher = GlobalEventDispatcher.getInstance();
  const state: IAppState = yield select();
  if (state.transientState.treuebonusReset) {
    globalEventDispatcher.dispatch(new GlobalActions().rabattHinweisChanged(true));
    yield put(new ToolbarActions().treuebonusResetChanged(false));
  }
}

export function vereinbarungenWatch(): void {
  const globalEventDispatcher = GlobalEventDispatcher.getInstance();
  globalEventDispatcher.dispatch(new GlobalActions().rabattHinweisChanged(false));
}

export function getNewTreuebonusArray(
  actionPayload: TreuebonusMaxReceived,
  oldTreuebonusArray: Array<Treuebonus>,
): Array<Treuebonus> {
  const newTreueBonusArray: Array<Treuebonus> = cloneDeep(oldTreuebonusArray);
  if (shouldRemoveTreuebonusItem(newTreueBonusArray, actionPayload)) {
    return newTreueBonusArray.filter(
      treuebonusItem =>
        !(
          treuebonusItem.appId === actionPayload.appId &&
          treuebonusItem.angebotsId === actionPayload.angebotsId
        ),
    );
  }

  if (shouldUpdateTreuebonusItem(newTreueBonusArray, actionPayload)) {
    return newTreueBonusArray.map(treuebonusItem => {
      if (
        treuebonusItem.appId === actionPayload.appId &&
        treuebonusItem.angebotsId === actionPayload.angebotsId
      ) {
        return {
          ...treuebonusItem,
          treuebonus_max: actionPayload.treuebonusMax,
          versicherung: actionPayload.versicherung,
          info: actionPayload.info,
          treuebonus_aktuell: 0,
          treuebonus_changed: 0,
        };
      }
      return treuebonusItem;
    });
  }

  if (actionPayload.treuebonusMax > 0) {
    return addTreuebonusItem(newTreueBonusArray, actionPayload);
  }

  return newTreueBonusArray;
}

function shouldRemoveTreuebonusItem(
  sectionState: Array<Treuebonus>,
  actionPayload: TreuebonusMaxReceived,
) {
  const currentTreuebonus = sectionState.find(
    treuebonusItem =>
      treuebonusItem.appId === actionPayload.appId &&
      treuebonusItem.angebotsId === actionPayload.angebotsId,
  );
  return currentTreuebonus && actionPayload.treuebonusMax === 0;
}

function shouldUpdateTreuebonusItem(
  sectionState: Array<Treuebonus>,
  actionPayload: TreuebonusMaxReceived,
) {
  const currentTreuebonus = sectionState.find(
    treuebonusItem =>
      treuebonusItem.appId === actionPayload.appId &&
      treuebonusItem.angebotsId === actionPayload.angebotsId,
  );
  return currentTreuebonus && actionPayload.treuebonusMax !== 0;
}

function addTreuebonusItem(
  sectionState: Array<Treuebonus>,
  actionPayload: TreuebonusMaxReceived,
): Array<Treuebonus> {
  return [
    ...sectionState,
    {
      appId: actionPayload.appId,
      angebotsId: actionPayload.angebotsId,
      treuebonus_max: actionPayload.treuebonusMax,
      versicherung: actionPayload.versicherung,
      info: actionPayload.info,
      treuebonus_aktuell: 0,
      treuebonus_changed: 0,
      laufzeit: 3,
    },
  ];
}
